import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Layout from "../../Components/layouts";
import { notify } from '../../utils';
import {
    applyProfileToPayload,
    emptyProfileFields,
    formatApiErrorMessage,
    profileFieldsFromUser,
    validateProfileFields,
} from '../../utils/userProfileFields';
import { REQUIRED_ERROR } from '../../utils/constants';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { UserServices, clearMyProfileCache } from '../../Services/UserServices';
import { CustomerServices } from '../../Services/CustomerServices';
import CircularProgress from '@material-ui/core/CircularProgress';
import AppLoader from '../../Components/AppLoader';
import ProfilePhotoField from '../../Components/ProfilePhotoField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { TextField } from '@material-ui/core';
import { useHistory } from 'react-router';
import SendTestEmailModal from 'Components/SendTestEmailModal';
import commonStyles, { pageEyebrowStyles, pageTitleTextStyles, surfaceCardStyles } from 'theme/commonStyles';
import { useImpersonation } from '../../hooks/useImpersonation';
import { ProjectServices } from '../../Services/ProjectServices';
import { normalizeProgrammeOptions, profileFieldsForProgramme } from '../../utils/programmeOptions';

const userServices = new UserServices();
const customerServices = new CustomerServices();
const projectServices = new ProjectServices();

const useStyles = makeStyles((theme) => ({
    pageHeader: {
        marginBottom: theme.spacing(2.5),
    },
    pageEyebrow: {
        ...pageEyebrowStyles,
        marginBottom: theme.spacing(0.5),
    },
    pageTitle: {
        ...pageTitleTextStyles,
        marginBottom: theme.spacing(0.5),
    },
    pageDescription: {
        color: '#647184',
        fontSize: '0.875rem',
        lineHeight: 1.55,
        maxWidth: 640,
    },
    detailsCard: {
        ...surfaceCardStyles,
        padding: theme.spacing(2.5),
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(2),
        },
    },
    sectionTitle: {
        color: '#263548',
        fontSize: '0.9375rem',
        fontWeight: 700,
        marginBottom: theme.spacing(2),
    },
    pageLoader: {
        minHeight: 280,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    btn: {
        padding: '12px 28px',
        fontSize: 14,
        fontWeight: 600,
        color: 'white',
        borderRadius: 8,
        textTransform: 'none',
        boxShadow: 'none',
        minWidth: 140,
        '&:hover': {
            boxShadow: 'none',
        },
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            padding: '12px 24px',
        },
    },
    cancelBtn: {
        padding: '12px 28px',
        fontSize: 14,
        fontWeight: 600,
        color: '#647184',
        borderRadius: 8,
        textTransform: 'none',
        border: '1px solid #e7ebf0',
        background: '#fff',
        boxShadow: 'none',
        minWidth: 120,
        '&:hover': {
            background: '#f5f8fc',
            borderColor: '#d5dde8',
            boxShadow: 'none',
        },
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            padding: '12px 24px',
        },
    },
    placeBtn: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        paddingTop: theme.spacing(2.5),
        marginTop: theme.spacing(2.5),
        borderTop: '1px solid #e7ebf0',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column-reverse',
            gap: theme.spacing(1),
        },
    },
    smtpSection: {
        marginTop: theme.spacing(2.5),
        padding: theme.spacing(2.5),
        background: '#f5f8fc',
        borderRadius: 8,
        border: '1px solid #e7ebf0',
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
        },
    },
    smtpTitle: {
        marginBottom: theme.spacing(2),
        fontSize: '0.9375rem',
        fontWeight: 700,
        color: '#263548',
        textAlign: 'left',
    },
    partnerActions: {
        marginTop: theme.spacing(2),
    },
    editThemeBtn: {
        marginLeft: 0,
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        borderColor: '#e7ebf0',
        color: '#263548',
        padding: '10px 20px',
        '&:hover': {
            borderColor: '#d5dde8',
            background: '#f5f8fc',
        },
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    checkboxLabel: {
        '& .MuiFormControlLabel-label': {
            color: '#263548',
            fontSize: '0.875rem',
        },
    },
    programmeCard: {
        ...surfaceCardStyles,
        marginTop: theme.spacing(2.5),
        padding: theme.spacing(2.5),
    },
    programmeMeta: {
        color: '#647184',
        fontSize: '0.8125rem',
        marginTop: theme.spacing(-1),
        marginBottom: theme.spacing(2),
    },
}));

const initFormState = {
    firstName: '',
    lastName: '',
    country: '',
    email: '',
    memberSince: '',
    profile_image: '',
    email_host: '',
    port: '22',
    from: '',
    email_host_user: '',
    email_host_password: '',
    email_subject_prifix: '',
    recipients: '',
    ...emptyProfileFields(),
};

const Profile = () => {

    const classes = useStyles();
    const commonClasses = commonStyles();

    const history = useHistory();
    const impersonation = useImpersonation();
    const [companyName, setCompanyName] = useState('');
    const [countryList, setCountryList] = useState([]);
    const [, setCountryName] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [showSMTP, setShowSMTP] = useState(false);
    const [formState, setFormState] = useState({ ...initFormState });
    const [partner, setPartner] = useState(false);
    const [sendTestMailOpen, setSendTestMailOpen] = useState(false);
    const [sendTestEmailLoading, setSendTestEmailLoading] = useState(false);
    const [programmeOptions, setProgrammeOptions] = useState(null);
    const [programmeMeta, setProgrammeMeta] = useState({ type: '', typeLabel: '', subtypeLabel: '', fieldKeys: [] });


    useEffect(() => {
        getUserProfile();
    }, []);

    async function getUserProfile() {
        try {
        const [user, optionsResult] = await Promise.all([
            userServices.getDetailMyProfile(),
            projectServices.getProgrammeOptions(),
        ]);
        if (optionsResult?.status) setProgrammeOptions(normalizeProgrammeOptions(optionsResult));
        if (user && user.data && user.data.length) {
            const userData = user.data[0];
            setProgrammeMeta({
                type: userData.programme_type || '',
                typeLabel: userData.programme_type_label || '',
                subtypeLabel: userData.programme_subtype_label || '',
                fieldKeys: Array.isArray(userData.programme_profile_fields) ? userData.programme_profile_fields : [],
            });
            setCountryName(userData.country);
            setFormState({
                firstName: userData.firstName,
                lastName: userData.lastName,
                email: userData.email,
                member_since: userData.member_since,
                profile_image: userData.profile_image,
                country: userData.country,
                email_host: userData.email_host,
                email_host_password: userData.email_host_password,
                email_host_user: userData.email_host_user,
                email_subject_prifix: userData.email_subject_prifix,
                smtp: userData.smtp,
                strategy_partner: userData.strategy_partner,
                port: "22",
                secure_connection: false,
                use_authentication: false,
                ...profileFieldsFromUser(userData),
            });
            setPartner(userData.strategy_partner)
            if (userData.smtp) {
                setShowSMTP(true);
            }
            const customer = user.data[0].customers && user.data[0].customers[0] ? user.data[0].customers[0].company_name : '';
            setCompanyName(customer);
        }
        const countryList = await customerServices.getCounties();
        setCountryList(countryList?.country_list || []);
        } catch (error) {
            notify('error', 'Could not load your profile.');
        } finally {
            setPageLoading(false);
        }
    }

    const handleInputChange = (evt) => {
        const { name, value } = evt.target;
        setFormState({ ...formState, [name]: value });
    }

    const formValidationCheck = () => {
        if (!formState.firstName || !formState.lastName || !formState.email || !formState.country) {
            notify("error", REQUIRED_ERROR);
            return null;
        }
        const programmeFields = profileFieldsForProgramme(
            programmeOptions,
            programmeMeta.type,
            programmeMeta.fieldKeys
        );
        const profileError = validateProfileFields(formState, {
            isEdit: true,
            programmeType: programmeMeta.type,
            profileFields: programmeFields,
        });
        if (profileError) {
            notify('error', profileError);
            return null;
        }
        return handleFormSubmit(formState);
    }

    const handleFormSubmit = async (formState) => {
        setLoading(true)
        const programmeFields = profileFieldsForProgramme(
            programmeOptions,
            programmeMeta.type,
            programmeMeta.fieldKeys
        );
        const payload = applyProfileToPayload(formState, formState, {
            isEdit: true,
            programmeType: programmeMeta.type,
            profileFields: programmeFields,
        });
        let result = await userServices.updateProfile(payload)
        if (result && result.status) {
            setLoading(false);
            clearMyProfileCache();
            notify("success", result.message);
            history.push('/dashboard');
        } else {
            notify("error", formatApiErrorMessage(result))
            setLoading(false)
        }
    }

    const handleUploadImage = (e) => {
        let file = e.target.files[0];
        if (!file) return;
        let reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
            setFormState({ ...formState, profile_image: reader.result })
        };
    }

    const handleChange = (value, field) => {
        setCountryName(value)
        setFormState({ ...formState, [field]: value });
    }

    const handleChangeCheck = () => {
        setShowSMTP(!showSMTP)
    }

    const handleChangeCheckItem = (name) => {
        if (name === "secure_connection") {
            setFormState({ ...formState, secure_connection: !formState.secure_connection })
        } else {
            setFormState({ ...formState, use_authentication: !formState.use_authentication })
        }
    }

    const editTheme = () => {
        const compId = localStorage.getItem('company_id');
        history.push(`/themeeditor/customer/${compId}`);
    }

    const handleSendEmailSubmit = async (recipients) => {
        setSendTestEmailLoading(true);
        let sendEmailData = { ...formState };

        let data = {
            email_host: sendEmailData.email_host,
            email_subject_prifix: sendEmailData.email_subject_prifix,
            email_host_user: sendEmailData.email_host_user,
            recipients: recipients
        };
        if (!impersonation.active) {
            data.email_host_password = sendEmailData.email_host_password;
        }
        const emailRes = await customerServices.sendTestEmail(data);
        if (emailRes && emailRes.status) {
            notify('success', emailRes?.message || "Email send successfully");
        } else {
            notify("error", (emailRes && emailRes.message) ? emailRes.message : 'Something Went Wrong');
        }
        setSendTestEmailLoading(false);
    }

    const programmeProfileFields = profileFieldsForProgramme(
        programmeOptions,
        programmeMeta.type,
        programmeMeta.fieldKeys
    );

    return (
        <Layout>
            <main>
                <div className={commonClasses.contentWrapper}>
                    <div className={classes.pageHeader}>
                        <Typography className={classes.pageEyebrow}>Account</Typography>
                        <Typography className={classes.pageTitle}>Profile</Typography>
                        <Typography className={classes.pageDescription}>
                            Manage your personal details and account preferences.
                        </Typography>
                    </div>
                    {pageLoading ? (
                        <AppLoader minHeight={280} />
                    ) : (
                    <>
                    <div className={classes.detailsCard}>
                        <Typography className={classes.sectionTitle}>Personal details</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <ProfilePhotoField
                                    src={formState.profile_image}
                                    inputId="profile-photo-input"
                                    onChange={handleUploadImage}
                                    accept="image/*"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="First Name"
                                    value={formState.firstName}
                                    type="text"
                                    name="firstName"
                                    onChange={handleInputChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Last Name"
                                    value={formState.lastName}
                                    type="text"
                                    name="lastName"
                                    onChange={handleInputChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Member Since"
                                    value={formState.member_since}
                                    type="text"
                                    name="member_since"
                                    disabled
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Email"
                                    value={formState.email}
                                    type="text"
                                    disabled
                                    name="email"
                                    onChange={handleInputChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Company Name"
                                    value={companyName}
                                    type="text"
                                    disabled
                                    name="customer"
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Autocomplete
                                    id="combo-box-demo4"
                                    value={formState.country}
                                    options={countryList ? countryList : []}
                                    getOptionLabel={option => option}
                                    onChange={(event, newValue) => {
                                        handleChange(newValue, 'country');
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Country"
                                            variant="outlined"
                                            size="small"
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </div>
                    {!!programmeMeta.type && programmeProfileFields.length > 0 && (
                        <div className={classes.programmeCard}>
                            <Typography className={classes.sectionTitle}>Programme profile</Typography>
                            <Typography className={classes.programmeMeta}>
                                {[programmeMeta.typeLabel, programmeMeta.subtypeLabel].filter(Boolean).join(' · ')}
                            </Typography>
                            <Grid container spacing={2}>
                                {programmeProfileFields.map(field => (
                                    <Grid item xs={12} sm={6} md={field.fullWidth ? 12 : 4} key={field.name}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            label={field.label}
                                            name={field.name}
                                            value={formState[field.name] ?? ''}
                                            multiline={!!field.multiline}
                                            rows={field.multiline ? 3 : undefined}
                                            onChange={handleInputChange}
                                            inputProps={{
                                                maxLength: field.maxLength,
                                                inputMode: field.type === 'integer' ? 'numeric' : undefined,
                                                min: field.min,
                                                max: field.max,
                                            }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </div>
                    )}
                    {partner && (<React.Fragment>
                        <Grid container spacing={2} className={classes.partnerActions}>
                            <Grid item xs={12}>
                                <FormGroup>
                                    <FormControlLabel
                                        className={classes.checkboxLabel}
                                        control={<Checkbox color="primary" checked={showSMTP ? true : false} onChange={() => handleChangeCheck('smtp')} name="smtp" />}
                                        label="SMTP Settings"
                                    />
                                </FormGroup>
                            </Grid>
                        </Grid>
                        {showSMTP && (
                            <div className={classes.smtpSection}>
                                <Typography className={classes.smtpTitle}>
                                    SMTP details
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="SMTP Server"
                                            value={formState.email_host}
                                            type="text"
                                            name="email_host"
                                            onChange={handleInputChange}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Port"
                                            value={formState.port}
                                            type="text"
                                            name="smtp"
                                            onChange={handleInputChange}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Email Account"
                                            value={formState.from}
                                            type="text"
                                            name="from"
                                            onChange={handleInputChange}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Mail Subject"
                                            value={formState.email_subject_prifix}
                                            type="text"
                                            name="email_subject_prifix"
                                            onChange={handleInputChange}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormGroup>
                                            <FormControlLabel
                                                className={classes.checkboxLabel}
                                                control={<Checkbox color="primary" checked={formState.secure_connection ? true : false} onChange={() => handleChangeCheckItem('secure_connection')} name="secure_connection" />}
                                                label="Use Secure Connection"
                                            />
                                            <FormControlLabel
                                                className={classes.checkboxLabel}
                                                control={<Checkbox color="primary" checked={formState.use_authentication ? true : false} onChange={() => handleChangeCheckItem('use_authentication')} name="use_authentication" />}
                                                label="Use Authentication"
                                            />
                                        </FormGroup>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Login"
                                            value={formState.email_host_user}
                                            type="text"
                                            name="email_host_user"
                                            onChange={handleInputChange}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    {!impersonation.active && (
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Password"
                                            value={formState.email_host_password}
                                            type="password"
                                            name="email_host_password"
                                            onChange={handleInputChange}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <Button
                                            type="button"
                                            onClick={() => setSendTestMailOpen(true)}
                                            variant="contained"
                                            color="primary"
                                            className={classes.btn}
                                            disableElevation
                                        >
                                            Send Test Email
                                        </Button>
                                    </Grid>
                                </Grid>
                            </div>
                        )}
                    </React.Fragment>
                    )}
                    {partner && (
                        <Grid container spacing={2} className={classes.partnerActions}>
                            <Grid item xs={12} sm={6} md={4}>
                                <Button className={classes.editThemeBtn} variant="outlined" onClick={editTheme}>
                                    Edit Theme
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                    </>
                    )}
                    {!pageLoading && (
                    <div className={classes.placeBtn}>
                        <Button onClick={() => {
                            history.push('/dashboard');
                        }} variant="outlined" className={classes.cancelBtn}>
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            variant="contained"
                            className={classes.btn}
                            onClick={formValidationCheck}
                            disabled={loading}
                            disableElevation
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : 'Update Profile'}
                        </Button>
                    </div>
                    )}
                </div>
                <SendTestEmailModal
                    open={sendTestMailOpen}
                    handleClose={evt => setSendTestMailOpen(false)}
                    handleSubmit={handleSendEmailSubmit}
                    loading={sendTestEmailLoading}
                />
            </main>
        </Layout>
    );
}

export default Profile;
